import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus} from 'platform/components';

import {isNotNil} from 'ramda';

import {useGetAdditionalNoteQuery, useGetVehicleV2Query} from '@dms/api/sales';
import {useGetServiceVehicleV2Query} from '@dms/api/workshop';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleRecommendationType} from '../../types/VehicleRecommendationType';
import {ServiceVehicleFormLogic} from './components/ServiceVehicleFormLogic';
import {ServiceVehicleFormMode} from './types/ServiceVehicleFormMode';
import {ServiceVehicleFormType} from './types/ServiceVehicleFormType';
import {ServiceVehicleFormVariant} from './types/ServiceVehicleFormVariant';
import {convertVehicleToFormData} from './utils/convertVehicleToFormData';

interface ServiceVehicleFormProps extends RequiredTestIdProps {
  mode: ServiceVehicleFormMode;
  variant: ServiceVehicleFormVariant;
  vehicleId?: string;
  extendedDefaultFormData?: Partial<ServiceVehicleFormType>;
  shouldRecommendVehiclesFor?: VehicleRecommendationType;
  onSuccess: (newVehicleId: string) => void;
  onDiscard: () => void;
}

export function ServiceVehicleForm(props: ServiceVehicleFormProps) {
  const skipFetchingVehicleDetail = props.mode === ServiceVehicleFormMode.NEW;

  const vehicleDetailQuery = useGetVehicleV2Query(
    {vehicleId: props.vehicleId || ''},
    {skip: skipFetchingVehicleDetail}
  );

  const additionalNoteQuery = useGetAdditionalNoteQuery(
    {vehicleId: props.vehicleId || ''},
    {skip: skipFetchingVehicleDetail}
  );

  // Vehicle class is only editable in the shortened dialog's edit mode - skip the request elsewhere.
  const serviceVehicleQuery = useGetServiceVehicleV2Query(
    props.variant === ServiceVehicleFormVariant.SIMPLE &&
      props.mode === ServiceVehicleFormMode.EDIT &&
      isNotNil(props.vehicleId)
      ? {vehicleId: props.vehicleId}
      : skipToken
  );

  const isLoading =
    vehicleDetailQuery.isLoading || additionalNoteQuery.isLoading || serviceVehicleQuery.isLoading;
  const isError = vehicleDetailQuery.isError; // we don't need note to success. If it's empty BE returns 404 anyway

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <ServiceVehicleFormLogic
        mode={props.mode}
        variant={props.variant}
        vehicleData={vehicleDetailQuery.data!}
        shouldRecommendVehiclesFor={props.shouldRecommendVehiclesFor}
        serviceVehicleId={serviceVehicleQuery.data?.serviceVehicleId}
        defaultFormData={{
          ...convertVehicleToFormData(vehicleDetailQuery.data, additionalNoteQuery.data?.note),
          vehicleClassId: serviceVehicleQuery.data?.vehicleClassId,
        }}
        extendedDefaultFormData={props.extendedDefaultFormData}
        onSuccess={props.onSuccess}
        onDiscard={props.onDiscard}
        data-testid={suffixTestId('seve', props)}
      />
    </DataStatus>
  );
}
